// src/components/dashboard/charts/StackedBarPlot.jsx
import Plot from "react-plotly.js";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { premiumLayout, premiumConfig, premiumPalette } from "./plotTheme";
import { usePlotlyAutoResize } from "./usePlotlyAutoResize";

export default function StackedYesNoPlot({ title, items = [] }) {
  const { t } = useTranslation();
  const { wrapperRef, plotDivRef } = usePlotlyAutoResize();

  const labelByKey = useMemo(
    () => ({
      bloc2_vegetation: t("dashboard.blocks.bloc2_vegetation", "Vegetació"),
      bloc3_rockBool: t("dashboard.blocks.bloc3_rockBool", "Roca / pedres"),
      bloc4_animalBool: t("dashboard.blocks.bloc4_animalBool", "Animals"),
      bloc5_wasteObserved: t("dashboard.blocks.bloc5_wasteObserved", "Residus"),
      bloc6_fungiBool: t("dashboard.blocks.bloc6_fungiBool", "Fongs"),
      bloc7_waterBool: t("dashboard.blocks.bloc7_waterBool", "Aigua"),
    }),
    [t]
  );

  const labels = useMemo(
    () => items.map((i) => labelByKey[i.key] || i.key),
    [items, labelByKey]
  );

  const yes = useMemo(() => items.map((i) => i.yes || 0), [items]);
  const no = useMemo(() => items.map((i) => i.no || 0), [items]);

  const emptyTotal = useMemo(
    () => items.reduce((acc, i) => acc + (i.empty || 0), 0),
    [items]
  );

  const emptyByBlock = useMemo(
    () =>
      items
        .filter((i) => (i.empty || 0) > 0)
        .map((i) => ({
          key: i.key,
          label: labelByKey[i.key] || i.key,
          empty: i.empty || 0,
        })),
    [items, labelByKey]
  );

  const data = useMemo(
    () => [
      {
        type: "bar",
        name: t("common.yes", "Sí"),
        x: labels,
        y: yes,
        marker: { color: premiumPalette.yes, opacity: 0.92 },
        hovertemplate: `${t("common.yes", "Sí")}: <b>%{y}</b><extra></extra>`,
      },
      {
        type: "bar",
        name: t("common.no", "No"),
        x: labels,
        y: no,
        marker: { color: premiumPalette.no, opacity: 0.88 },
        hovertemplate: `${t("common.no", "No")}: <b>%{y}</b><extra></extra>`,
      },
    ],
    [labels, yes, no, t]
  );

  const layout = useMemo(() => {
    const L = premiumLayout();

    return {
      ...L,
      barmode: "stack",
      autosize: true,
      height: 360, // OK si el card tiene altura estable; si quieres 100% height, quita esto
      margin: { l: 48, r: 14, t: 10, b: 74 },
      bargap: 0.28,

      xaxis: {
        ...L.xaxis,
        automargin: true,
        tickangle: -15, // ayuda en pantallas medianas/pequeñas
        ticklabeloverflow: "allow",
      },
      yaxis: {
        ...L.yaxis,
        title: { text: t("dashboard.count", "Recompte"), standoff: 10 },
        automargin: true,
        rangemode: "tozero",
      },

      legend: {
        ...L.legend,
        orientation: "h",
        y: 1.22,
      },
    };
  }, [t]);

  return (
    <div
      ref={wrapperRef}
      className="
        min-w-0
        rounded-2xl border border-slate-200/70 bg-white
        shadow-[0_1px_0_rgba(15,23,42,0.04),0_8px_24px_rgba(15,23,42,0.08)]
        p-4
      "
    >
      <div className="font-semibold text-slate-900">{title}</div>

      <div className="text-xs text-slate-500 mb-2">
        {t("dashboard.yesno.subtitle", "Sí/No per bloc (segons formularis)")}
      </div>

      <div className="min-w-0">
        <Plot
          data={data}
          layout={layout}
          config={premiumConfig}
          useResizeHandler
          style={{ width: "100%" }}
          onInitialized={(_, gd) => {
            // gd es el div interno (graph div)
            plotDivRef.current = gd;
          }}
        />
      </div>

      {/* Nota divulgativa */}
      <div className="text-xs text-slate-500 mt-2">
        {t("dashboard.empty.note", "Respostes buides")}:{" "}
        <span className="font-medium text-slate-800">{emptyTotal}</span>
      </div>

      {/* Detall per bloc (bien “wrap”, con separadores) */}
      {emptyByBlock.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
          {emptyByBlock.map((b) => (
            <span key={b.key} className="whitespace-nowrap">
              {b.label}: {b.empty}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}